import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";

interface Props {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

export default function CameraCapture({ open, onClose, onCapture }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    if (!open) return;

    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment", // back camera on mobile
          },
        });

        setStream(mediaStream);

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error(err);
      }
    };

    startCamera();

    return () => {
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [open]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) return;

      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      onCapture(file);
      handleClose();
    }, "image/jpeg");
  };

  const handleClose = () => {
    stream?.getTracks().forEach((track) => track.stop());
    onClose();
  };

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: "black",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          playsInline
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        <canvas ref={canvasRef} hidden />
      </DialogContent>

      <DialogActions
        sx={{
          position: "absolute",
          bottom: 20,
          width: "100%",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          sx={{ color: "white" }}
        >
          Cancel
        </Button>

        <Button variant="contained" onClick={capture}>
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );
}
